import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAiIngest, useAiIngestExistingRows } from "../../../hooks/query/questionnaire/ai-ingest";
import { useAiIngestTemplateQuery } from "../../../hooks/query/questionnaire";
import {
  useGetQuestionnaireById,
  useUpdateMethodology,
} from "../../../hooks/query/questionnaire/organization-questionnaire";
import {
  appendDraft,
  currentMethodologyFor,
  methodologyAfterCommit,
  methodologySaveKeyFor,
  taskMethodologyFor,
} from "../components/methodologyMode";
import useLocalStorage from "hooks/useLocalStorage";
import { buildCommitPayload, fetchDataFrameData } from "../api/dataMappingClient";
import { generateMethodologyViaChat } from "../api/generateMethodologyViaChat";
import { requestWithMethodology } from "../components/commitMethodology";
import { describeIngestError } from "../components/describeIngestError";
import { formatPeriod, formatTarget, pinnedDimensions, scopePillFor } from "../components/taskLabels";
import { markdownToHtml } from "utils/text-utils";
import { useCommitStatus } from "./useCommitStatus";
import { useExistingRowCount } from "./useExistingRowCount";
import { alignExistingRows } from "../components/existingRows";
import { useSession } from "../state/useSession";
import { useWorkingPanel } from "../state/useWorkingPanel";

const OUTPUT_PREVIEW_PAGE_SIZE = 100;

/**
 * Everything the Commit stage (nav chip, Preview, rail) needs, derived once so each component stays a
 * plain renderer. Mirrors the brief's §6 list almost exactly, with two pieces honestly stubbed rather than
 * fabricated:
 *
 * - `rowState`/`changes` need a stable row id to compare against the last commit's rows, and nothing in
 *   the pipeline carries one — a mapped frame's rows are positional, re-ordered freely by every chat turn.
 *   TODO(backend): need stable row ids per commit. Until then this always reports "nothing to classify",
 *   which is honest (it never invents a new/removed split) rather than silently wrong.
 * - The task's existing rows come from `GET /ai-ingest/existing-rows`, which returns them flattened under
 *   the descriptor's field names with ids resolved to labels — the mapped frame's own shape — so Existing
 *   and After commit show real rows beside Ready to commit. Their headers are renamed to the frame's own
 *   column spellings through the payload's `column_by_field` (see ../components/existingRows), so the
 *   tables line up exactly.
 */
export function useCommitState({ sessionId }) {
  const { questionnaireId, aiIngestTarget, hasTaskTemplate } = useSession();
  const { selectedOutputKey, dataframes, outputCreatedCounter } = useWorkingPanel();

  const { count: existingCountFromGrid, isLoading: isLoadingExistingCount } = useExistingRowCount(
    questionnaireId,
    aiIngestTarget
  );
  const { data: descriptor } = useAiIngestTemplateQuery(
    { questionnaireId, target: aiIngestTarget },
    { enabled: Boolean(questionnaireId && aiIngestTarget) }
  );

  const [payload, setPayload] = useState(null);
  const [payloadError, setPayloadError] = useState(null);
  const [isBuilding, setBuilding] = useState(false);
  const [mode, setMode] = useState("APPEND");
  const [replaceAcknowledged, setReplaceAcknowledged] = useState(false);
  const [validated, setValidated] = useState(null);
  const [ingestError, setIngestError] = useState(null);
  const [committed, setCommitted] = useState(null);
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [isDryRunning, setDryRunning] = useState(false);
  const [isCommitting, setCommitting] = useState(false);

  // The ready-to-commit rows themselves, for the left-hand Preview table. Same call LiveOutputsView
  // already makes against this output frame — this is a second, independent fetch rather than shared
  // state, because the Live and Commit stages are never both mounted at once (WorkingPanel renders one
  // stage body at a time) so there is nothing to deduplicate.
  const [outputRows, setOutputRows] = useState(null);
  const [isLoadingOutputRows, setLoadingOutputRows] = useState(false);
  // 1-based, like the /dataframes envelope both tables page through. Separate pages, because the After
  // commit view shows both lists at once and each pages on its own.
  const [readyPage, setReadyPage] = useState(1);
  const [existingPage, setExistingPage] = useState(1);

  const [methodology, setMethodology, clearStoredMethodology] = useLocalStorage(
    `aiDataMapping.methodology.${sessionId}.${selectedOutputKey || "none"}`,
    ""
  );
  const [isGeneratingMethodology, setGeneratingMethodology] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  /*
    The methodology already saved on the task for this target's scope (`methodologyMap`, what the data task
    page shows). The editor follows the ROWS' write mode — see methodologyMode.js: Add to existing seeds it
    with this; Replace all rows empties it. It always shows what the task will hold after the commit.
  */
  const { data: questionnaire, refetch: refetchQuestionnaire } = useGetQuestionnaireById(questionnaireId, {
    enabled: Boolean(questionnaireId),
  });
  const existingMethodology = taskMethodologyFor(questionnaire, aiIngestTarget);
  const [methodologySaveError, setMethodologySaveError] = useState(null);
  const { mutateAsync: updateMethodology } = useUpdateMethodology();

  // The editor's latest content, for Generate to append to. A generation takes up to 90s, and the user can
  // keep typing meanwhile; appending to the value captured when Generate was clicked would discard that.
  const methodologyRef = useRef(methodology);
  methodologyRef.current = methodology;

  /*
    Seed the editor whenever the mode (or task/target) changes, and once the task's methodology has loaded:
    Add → the current methodology; Replace → empty. Keyed so it never overwrites the user's own edits within
    one mode — switching modes is the explicit "start over" for the statement, the same as for the rows.
  */
  const seededForRef = useRef(null);
  useEffect(() => {
    if (!questionnaire) return;
    const key = `${questionnaireId}|${aiIngestTarget}|${mode}`;
    if (seededForRef.current === key) return;
    seededForRef.current = key;
    setMethodology(mode === "REPLACE_TARGET" ? "" : existingMethodology);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaire, questionnaireId, aiIngestTarget, mode]);

  // Content hash of the output frame being committed. A re-map rebuilds the SAME frame key in place
  // (autoMapPrompt: "Rebuild X in place"), so keying the payload and the preview rows on the key alone
  // would keep showing the previous mapping — which mattered less when this panel lived only on the Commit
  // tab, and matters now that it is also the Preview tab, where re-mapping happens.
  const outputFingerprint = dataframes.find((d) => d.key === selectedOutputKey)?.fingerprint ?? "";

  // The same "has this been committed, has it changed since" status the stage nav reads — see
  // useCommitStatus for why this is a separate hook rather than local state here.
  const { lastCommit, setLastCommit, isUpToDate, hasChangedSinceCommit, navChip } = useCommitStatus({
    sessionId,
    selectedOutputKey,
    outputCreatedCounter,
  });

  const loadPayload = useCallback(async () => {
    if (!selectedOutputKey) {
      setPayload(null);
      return;
    }
    setBuilding(true);
    setPayloadError(null);
    setValidated(null);
    setIngestError(null);
    setCommitted(null);
    try {
      const result = await buildCommitPayload(sessionId, selectedOutputKey, { mode });
      setPayload(result);
    } catch (error) {
      setPayload(null);
      setPayloadError(
        error?.response?.status === 404
          ? "That mapped output no longer exists in this session. Refresh and pick it again."
          : "Could not prepare the data for commit. Try refreshing."
      );
    } finally {
      setBuilding(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- outputFingerprint: refetch on an in-place rebuild
  }, [sessionId, selectedOutputKey, mode, outputFingerprint]);

  useEffect(() => {
    loadPayload();
  }, [loadPayload]);

  useEffect(() => {
    setReplaceAcknowledged(false);
  }, [mode]);

  // The rows themselves, for Preview — independent of `mode`, so flipping Add/Replace never re-fetches it.
  useEffect(() => {
    if (!selectedOutputKey) {
      setOutputRows(null);
      return undefined;
    }
    let cancelled = false;
    setLoadingOutputRows(true);
    fetchDataFrameData(sessionId, selectedOutputKey, readyPage, OUTPUT_PREVIEW_PAGE_SIZE)
      .then((data) => {
        if (!cancelled) setOutputRows(data);
      })
      .catch(() => {
        if (!cancelled) setOutputRows(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingOutputRows(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, selectedOutputKey, outputFingerprint, readyPage]);

  // Back to the first page whenever the frame being previewed changes, or is rebuilt in place.
  useEffect(() => {
    setReadyPage(1);
  }, [selectedOutputKey, outputFingerprint]);

  const { data: existingRowsRaw, isLoading: isLoadingExistingRows } = useAiIngestExistingRows({
    questionnaireId,
    target: aiIngestTarget,
    page: existingPage,
    size: OUTPUT_PREVIEW_PAGE_SIZE,
  });
  // Headers renamed to the mapped frame's own spellings, so the Existing table and the Ready table share
  // column names; columns the frame has and the target lacks show blank.
  const existingRows = useMemo(
    () => alignExistingRows(existingRowsRaw, outputRows?.columns, payload?.column_by_field),
    [existingRowsRaw, outputRows?.columns, payload?.column_by_field]
  );
  // The rows endpoint counts exactly what a REPLACE deletes, so once it has answered it is the number to
  // trust; the grid count is the fallback while it loads (and the only one an older backend gives).
  const existingRowCount = existingRowsRaw ? existingRowsRaw.total_rows : existingCountFromGrid;

  const { mutateAsync: runIngest, isLoading: isIngesting } = useAiIngest();

  const runDryRun = useCallback(async () => {
    if (!payload?.ingest_request) return false;
    setDryRunning(true);
    setIngestError(null);
    setValidated(null);
    try {
      const response = await runIngest({
        questionnaireId,
        request: payload.ingest_request,
        dryRun: true,
      });
      setValidated({
        request: payload.ingest_request,
        rowsAccepted: response.rowsAccepted,
        liveRowCount: response.rowsReplaced,
      });
      return true;
    } catch (error) {
      setIngestError(describeIngestError(error, { dryRun: true }));
      return false;
    } finally {
      setDryRunning(false);
    }
  }, [payload, questionnaireId, runIngest]);

  useEffect(() => {
    if (!payload?.ingest_request || committed) return;
    runDryRun();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  const handleGenerateDraft = async () => {
    setGenerateError(null);
    setGeneratingMethodology(true);
    try {
      const markdown = await generateMethodologyViaChat(sessionId, {
        target: payload?.target ?? aiIngestTarget,
      });
      // Straight into the editor, after what is already there — in Add mode that is the task's previous
      // methodology, in Replace mode the editor started empty — so the user edits the whole statement in
      // one place before committing. See methodologyMode.appendDraft.
      setMethodology(appendDraft(methodologyRef.current, markdownToHtml(markdown)));
    } catch (error) {
      setGenerateError(error?.message || "Could not generate a methodology statement.");
    } finally {
      setGeneratingMethodology(false);
    }
  };

  const handleCommit = async () => {
    if (!validated) return;
    setCommitting(true);
    setIngestError(null);
    try {
      const request =
        mode === "REPLACE_TARGET"
          ? { ...validated.request, expectedReplacedRows: validated.liveRowCount }
          : validated.request;
      // An EMPTY summary: the backend's own section then holds only its record line, and the editor's
      // content is saved below as the scope's whole methodology — exactly what the editor showed.
      const finalRequest = requestWithMethodology(request, "");
      const response = await runIngest({ questionnaireId, request: finalRequest, dryRun: false });
      setCommitted(response);
      setMethodologySaveError(null);
      const target = response?.target ?? aiIngestTarget;
      // Saved under the target's OWN key (Scope 1 → its type tab); the backend's fresh record is read from
      // the shared scope key, which is still where the commit writes it. TODO(backend): write per type.
      const saveKey = methodologySaveKeyFor(target);
      if (saveKey) {
        try {
          const fresh = (await refetchQuestionnaire())?.data;
          const saved = currentMethodologyFor(fresh, target);
          await updateMethodology({
            questionnaireId,
            data: { scopeKey: saveKey, methodology: methodologyAfterCommit(methodology, saved, target) },
          });
        } catch (error) {
          // The rows are in; only the statement failed. Say so rather than leave it looking saved.
          setMethodologySaveError(
            "The data was committed, but the methodology could not be saved. Add it on the data task page."
          );
        }
      }
      refetchQuestionnaire();
      setValidated(null);
      setMethodology("");
      clearStoredMethodology();
      seededForRef.current = null;
      setLastCommit({
        at: Date.now(),
        rowsWritten: response.rowsAccepted ?? 0,
        rowsReplaced: response.rowsReplaced ?? 0,
        outputCreatedCounter,
      });
      return response;
    } catch (error) {
      setIngestError(describeIngestError(error, { dryRun: false }));
      return null;
    } finally {
      setCommitting(false);
    }
  };

  const handleConfirmYes = async () => {
    const stillValid = await runDryRun();
    if (!stillValid) {
      setConfirmOpen(false);
      return null;
    }
    const response = await handleCommit();
    setConfirmOpen(false);
    return response;
  };

  const issues = payload?.issues ?? [];
  const notCommittable = payload && !payload.committable;
  const replaceBlocked = mode === "REPLACE_TARGET" && !replaceAcknowledged;
  // A generation still writing would land in the editor after the commit had already saved its content.
  const canCommit = Boolean(validated) && !replaceBlocked && !isIngesting && !isGeneratingMethodology;
  const targetLabel = formatTarget(payload?.target ?? aiIngestTarget);
  const period = formatPeriod(descriptor);
  const dimensions = pinnedDimensions(descriptor);
  const scopePill = scopePillFor(payload?.target ?? aiIngestTarget);
  const hasExisting = (existingRowCount ?? 0) > 0;
  const mappedCount = payload?.row_count ?? 0;
  const isReplace = mode === "REPLACE_TARGET";
  const rowsAfter = isReplace ? mappedCount : (existingRowCount ?? 0) + mappedCount;

  // Row-level New/Committed/Removed classification (brief §5/§6) needs a stable id per row, which no
  // frame in this pipeline carries — rows are positional arrays, re-shuffled by every chat turn. Rather
  // than guess a split (which would show a confident, wrong answer), this reports that it CANNOT classify:
  // `changes` is null (not a count) whenever there is something changed but nothing to tell apart.
  // TODO(backend): need stable row ids per commit, so a row can be compared against lastCommit's rows.
  const canClassifyRowChanges = false;
  const changes = hasChangedSinceCommit ? null : 0;

  return {
    // Identity / descriptor
    targetLabel,
    period,
    dimensions,
    scopePill,
    // Rows
    outputRows,
    isLoadingOutputRows,
    readyPage,
    setReadyPage,
    existingRows,
    isLoadingExistingRows,
    existingPage,
    setExistingPage,
    existingRowCount,
    isLoadingExistingCount,
    hasExisting,
    mappedCount,
    isBuilding,
    canClassifyRowChanges,
    changes,
    // Payload / validation
    payload,
    payloadError,
    issues,
    notCommittable,
    validated,
    isDryRunning,
    ingestError,
    // Mode (Add/Replace)
    mode,
    setMode,
    isReplace,
    replaceAcknowledged,
    setReplaceAcknowledged,
    replaceBlocked,
    rowsAfter,
    // Methodology
    methodology,
    setMethodology,
    existingMethodology,
    methodologySaveError,
    isGeneratingMethodology,
    generateError,
    handleGenerateDraft,
    // Commit flow
    canCommit,
    isCommitting,
    isConfirmOpen,
    setConfirmOpen,
    handleConfirmYes,
    committed,
    // Session / task state
    questionnaireId,
    aiIngestTarget,
    hasTaskTemplate,
    selectedOutputKey,
    dataframes,
    // "Up to date" / changed-since-commit
    lastCommit,
    isUpToDate,
    hasChangedSinceCommit,
    navChip,
  };
}

export default useCommitState;
